import { PlaycastMessage, PlaycastMessageStateReplication, PlaycastMessageTarget, generators } from '@playcastdotio/Messaging';
import * as Sentry from '@sentry/react';
import { getElectronStores } from '../../stores/electronStores';
import { getSharedStores } from '../../stores/shared';
import { addReplicationStateChangeCallback, getNotifyMessages } from '@playcastdotio/StateStore';
import { RealtimeReconnectPolicy } from '../../../services/realtime/RealtimeReconnectPolicy';
import { NodeServiceCredentialService } from './NodeServiceCredentialService';
import { type NodeServiceMessageHandler, NodeServiceMessageHandlers } from './NodeServiceMessageHandlers';

const PORT = 27123;
const HEARTBEAT_INTERVAL = 20000; // 20 seconds between pings (match server config)

let wsConnection: WebSocket | null = null;
let isConnected = false;
let messageQueue: PlaycastMessage<PlaycastMessageTarget>[] = [];
let heartbeatInterval: NodeJS.Timeout | null = null;
let reconnectTimeout: NodeJS.Timeout | null = null;
let isReconnecting = false;
let shouldAutoReconnect = true;
const baseReconnectDelay = 1000; // 1 second base delay
const maximumReconnectDelay = 30000;
const reconnectPolicy = new RealtimeReconnectPolicy(baseReconnectDelay, maximumReconnectDelay);
const credentialService = new NodeServiceCredentialService(PORT);
let isOpening = false;
let connectGeneration = 0;
type NodeServiceMessage = PlaycastMessage<PlaycastMessageTarget>;
type MessageHandler = NodeServiceMessageHandler<NodeServiceMessage>;
const messageHandlers = new NodeServiceMessageHandlers<NodeServiceMessage>();

const clearReconnectTimeout = () => {
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }
};

const startHeartbeat = () => {
  stopHeartbeat();

  heartbeatInterval = setInterval(() => {
    if (!wsConnection || wsConnection.readyState !== WebSocket.OPEN) {
      Sentry.addBreadcrumb({
        category: 'node-service',
        message: 'Heartbeat detected invalid websocket',
        level: 'warning',
        data: { readyState: wsConnection?.readyState ?? null },
      });
      stopHeartbeat();
      disconnect();
      if (shouldAutoReconnect) {
        handleReconnect();
      }
      return;
    }

    try {
      const heartbeatMessage = generators.service.ping('experimental');
      wsConnection.send(JSON.stringify(heartbeatMessage));
    } catch (error) {
      console.error('[Node Service Client] Failed to send heartbeat:', error);
      Sentry.addBreadcrumb({
        category: 'node-service',
        message: 'Heartbeat send failed',
        level: 'error',
        data: { error: error?.toString() || 'Unknown error' },
      });
      stopHeartbeat();
      disconnect();
      if (shouldAutoReconnect) {
        handleReconnect();
      }
    }
  }, HEARTBEAT_INTERVAL);
};

const stopHeartbeat = () => {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
};

const handleReconnect = () => {
  if (!shouldAutoReconnect) {
    console.log('[Node Service Client] Auto-reconnect disabled');
    return;
  }

  if (isReconnecting) {
    console.log('[Node Service Client] Reconnection already in progress');
    return;
  }

  isReconnecting = true;
  const decision = reconnectPolicy.next();

  console.log(`[Node Service Client] Attempting to reconnect (attempt ${decision.attempt}) in ${decision.delayMs}ms...`);

  disconnect();

  // Wait before attempting to reconnect
  clearReconnectTimeout();
  reconnectTimeout = setTimeout(() => {
    try {
      // Reset wsConnection to null before attempting to reconnect
      wsConnection = null;
      connect();
    } catch (error) {
      console.error('[Node Service Client] Reconnection attempt failed:', error);
      // Don't call handleReconnect recursively here, let the connection failure handle it
    } finally {
      isReconnecting = false;
    }
  }, decision.delayMs);
};

const processQueue = () => {
  while (messageQueue.length > 0 && isConnected && wsConnection?.readyState === WebSocket.OPEN) {
    const message = messageQueue.shift();
    if (message) {
      try {
        wsConnection.send(JSON.stringify(message));
        console.log('[Node Service Client] Sent queued message:', message.header.action);
      } catch (error) {
        console.error('[Node Service Client] Failed to send queued message:', error);
        messageQueue.unshift(message); // Put the message back at the start of the queue
        break;
      }
    }
  }
};

const electronState = getElectronStores();
const sharedStateStores = getSharedStores();
const handleIncomingMessage = (message: PlaycastMessage<PlaycastMessageTarget>) => {
  console.log('[Node Service Client] Received message:', message.header.action, 'target:', message.header.target);

  // A message arriving is the first evidence the connection is genuinely
  // usable, as opposed to merely open (see the note in `onopen`). node-service
  // flushes `getNotifyMessages(['hostFrontend'])` to every client the moment it
  // accepts one, so an authorized peer lands here immediately; an unauthorized
  // one never does and keeps its accumulating backoff.
  if (reconnectPolicy.attemptCount !== 0) {
    console.log('[Node Service Client] Connection confirmed by first message; resetting reconnect backoff');
    reconnectPolicy.reset();
  }

  if (message.header.target === 'state' && message.header.action === 'replication') {
    console.log('[Node Service Client] Received replication message:', message);
    const stateMessage = message as PlaycastMessage<PlaycastMessageStateReplication<any>>;
    const channel = stateMessage.body.message.channel;
    if (electronState[channel]) {
      electronState[channel].updateState(stateMessage.body.message.state as never);
    } else if (sharedStateStores[channel]) {
      sharedStateStores[channel].updateState(stateMessage.body.message.state as never);
    } else {
      console.error('[Node Service Client] Invalid channel:', channel);
    }
    return;
  }

  messageHandlers.dispatch(message);
};

const openConnection = (token: string | null) => {
  const wsUrl = credentialService.buildConnectionUrl(token);
  console.log('[Node Service Client] Connecting to: ws://localhost:' + PORT);

  try {
    wsConnection = new WebSocket(wsUrl);

    wsConnection.onopen = () => {
      console.log('[Node Service Client] Connected to node service');

      // NOT reset here. `onopen` only proves a TCP+upgrade handshake completed,
      // which is not the same as being authenticated: node-service can accept
      // the socket and then close it, and a rejection that arrives after OPEN
      // would reset the backoff on every cycle — turning the capped exponential
      // retry below into an uncapped ~1 Hz loop that never stops and logs two
      // errors per cycle. The counter is cleared in handleIncomingMessage
      // instead, on the first message that actually arrives from the service,
      // which is proof the connection is usable.

      isConnected = true;
      startHeartbeat();
      processQueue();
      const replicationMessages = getNotifyMessages(['nodeService']);
      replicationMessages.nodeService.forEach((message) => {
        console.log('[Node Service Client] Sending replication message:', message.header.action);
        sendMessage(message);
      });
    };

    wsConnection.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as PlaycastMessage<PlaycastMessageTarget>;
        handleIncomingMessage(message);
      } catch (error) {
        console.error('[Node Service Client] Failed to parse message:', error);
      }
    };

    wsConnection.onclose = (event) => {
      console.log('[Node Service Client] Connection closed:', event.code, event.reason);
      isConnected = false;
      stopHeartbeat();

      // Always attempt reconnect if auto-reconnect is enabled
      if (shouldAutoReconnect && !isReconnecting) {
        console.log('[Node Service Client] Connection lost, attempting to reconnect...');
        handleReconnect();
      }
    };

    wsConnection.onerror = (error) => {
      console.error('[Node Service Client] WebSocket error:', error);
      // Don't call handleReconnect here - let onclose handle it
    };
  } catch (error) {
    console.error('[Node Service Client] Failed to connect:', error);
    if (shouldAutoReconnect) {
      handleReconnect();
    }
  }
};

export const connect = () => {
  if (wsConnection?.readyState === WebSocket.OPEN || wsConnection?.readyState === WebSocket.CONNECTING) {
    console.warn('[Node Service Client] Connection already exists');
    return;
  }
  if (isOpening) {
    console.warn('[Node Service Client] Connection attempt already in flight');
    return;
  }

  isOpening = true;
  const generation = ++connectGeneration;

  credentialService.resolve()
    .then((token) => {
      // A disconnect() (or another connect()) landed while we were reading the
      // token — that newer intent wins.
      if (generation !== connectGeneration) {
        console.log('[Node Service Client] Superseded connection attempt, not opening socket');
        return;
      }
      if (!token) {
        console.error(
          '[Node Service Client] No node-service auth token available; node-service will reject this connection. ' +
            'host-electron must expose window.playcast.nodeServiceAuthToken read from ' +
            'C:\\ProgramData\\Playcast\\node-service\\ws-auth-token.',
        );
      }
      openConnection(token);
    })
    .catch((error) => {
      console.error('[Node Service Client] Failed to start connection:', error);
      if (shouldAutoReconnect) {
        handleReconnect();
      }
    })
    .finally(() => {
      if (generation === connectGeneration) {
        isOpening = false;
      }
    });
};

export const disconnect = (disableAutoReconnect = false) => {
  console.log('[Node Service Client] Disconnecting...', disableAutoReconnect ? '(auto-reconnect disabled)' : '');

  if (disableAutoReconnect) {
    shouldAutoReconnect = false;
  }

  stopHeartbeat();
  clearReconnectTimeout();
  isConnected = false;
  messageQueue = [];
  // Invalidate any in-flight token read so its socket is never opened.
  connectGeneration++;
  isOpening = false;

  if (wsConnection) {
    // Store reference and null the connection before closing
    const conn = wsConnection;
    wsConnection = null;

    try {
      conn.close(1000, 'Client disconnect');
    } catch (error) {
      console.error('[Node Service Client] Error while closing connection:', error);
    }
  }
};

export interface SendMessageOptions {
  /**
   * When false, a message that can't be sent right now is dropped instead of
   * queued. Use this for periodic "latest snapshot wins" messages (e.g. a
   * software-inventory poll reply) where queueing while disconnected would
   * only accumulate stale snapshots to flush all at once on reconnect; the
   * next periodic poll will produce a fresher one anyway. Defaults to true
   * to preserve existing at-least-once delivery for other message types.
   */
  queueOnFailure?: boolean;
}

export const sendMessage = (
  message: PlaycastMessage<PlaycastMessageTarget>,
  options: SendMessageOptions = {},
) => {
  const { queueOnFailure = true } = options;

  if (!wsConnection || wsConnection.readyState !== WebSocket.OPEN || !isConnected) {
    if (queueOnFailure) {
      console.warn('[Node Service Client] Connection not ready, queueing message:', message.header.action);
      messageQueue.push(message);
    } else {
      console.warn('[Node Service Client] Connection not ready, dropping message:', message.header.action);
    }
    return false;
  }

  try {
    wsConnection.send(JSON.stringify(message));
    console.log('[Node Service Client] Sent message:', message.header.action);
    return true;
  } catch (error) {
    console.error('[Node Service Client] Failed to send message:', error);
    if (queueOnFailure) {
      messageQueue.push(message);
    }
    return false;
  }
};

export const onMessage = (action: string, handler: MessageHandler) => {
  messageHandlers.add(action, handler);
  console.log(`[Node Service Client] Registered handler for action: ${action}`);
};

export const offMessage = (action: string, handler: MessageHandler) => {
  if (messageHandlers.remove(action, handler)) {
    console.log(`[Node Service Client] Unregistered handler for action: ${action}`);
  }
};

export const clearMessageHandlers = (action?: string) => {
  messageHandlers.clear(action);
  console.log(action
    ? `[Node Service Client] Cleared handlers for action: ${action}`
    : '[Node Service Client] Cleared all message handlers');
};

export const isNodeServiceConnected = (): boolean => {
  return wsConnection?.readyState === WebSocket.OPEN && isConnected;
};

export const getConnectionStatus = () => {
  return {
    connected: isNodeServiceConnected(),
    reconnecting: isReconnecting,
    queueSize: messageQueue.length,
    autoReconnect: shouldAutoReconnect,
    reconnectAttempts: reconnectPolicy.attemptCount,
    maxReconnectAttempts: Number.POSITIVE_INFINITY,
    connectionState: wsConnection?.readyState ?? -1,
  };
};

export const setAutoReconnect = (enabled: boolean) => {
  shouldAutoReconnect = enabled;
  console.log(`[Node Service Client] Auto-reconnect ${enabled ? 'enabled' : 'disabled'}`);
};

export const enableAutoReconnect = () => {
  shouldAutoReconnect = true;
  reconnectPolicy.reset();
  console.log('[Node Service Client] Auto-reconnect enabled');
};

export const disableAutoReconnect = () => {
  shouldAutoReconnect = false;
  clearReconnectTimeout();
  console.log('[Node Service Client] Auto-reconnect disabled');
};

export const forceReconnect = () => {
  console.log('[Node Service Client] Forcing reconnection...');
  reconnectPolicy.reset();
  shouldAutoReconnect = true;
  handleReconnect();
};

addReplicationStateChangeCallback('nodeService', (message) => {
  console.log('[Node Service Client] Received replication message:', message);
  sendMessage(message);
});
